import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { BASE_URL } from '../../../constants';
import { secureFetch } from '../../../utils/secureFetch';
import { fetchUserByEmail } from '../../../store/actions/userActions';
import { fetchAllCodes } from '../../../store/actions/codeActions';
import CoachVerificationPanel from './CoachVerificationPanel';
import { Badge, Btn, Card, EmptyState, Input, Overline, PageHeader, Select } from '../../ForBrands/ui/BrandUI';
import '../../ForBrands/brandMode.css';

// Profile & verification in the V2 style. Every account that opens it does
// so inside a V2 shell: Rep Mode (/rep/profile), Brand Mode (/brand/profile)
// and the Player Index (/business/profile: recruiters, AD/PD and admins),
// where the layout supplies the frame. Only the admin coach editor, outside
// any shell, wraps the page in the same root so the treatment matches.
// Coaches get the team and verification cards; reps get their Rep ID and
// agency; every other role gets its account details and subscription.

// The shells the page can sit in, by path prefix.
const SHELL_PREFIXES = ['/business', '/brand', '/rep', '/coach'];
const inShellPath = (pathname = '') => SHELL_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`));

// The account's billing page inside the shell it is in, for the roles that
// pay for a plan. Reps and admins have no billing page. The /brand and
// /business billing pages take recruiters, brands and AD/PD only, so a coach
// who opens this page there (a coach who is also staff) keeps Coach Mode
// billing.
const BUSINESS_PLAN_ROLES = ['IS_RECRUITER_SCOUT', 'IS_BRAND_AGENCY', 'IS_AD_PD'];
export const billingPathIn = (pathname = '', permissions = []) => {
  const isCoach = permissions.includes('IS_COACH');
  const buysBusinessPlan = permissions.some((p) => BUSINESS_PLAN_ROLES.includes(p));
  if (!isCoach && !buysBusinessPlan) return null;
  if (pathname.startsWith('/coach')) return '/coach/billing';
  if (buysBusinessPlan && pathname.startsWith('/brand')) return '/brand/billing';
  if (buysBusinessPlan && pathname.startsWith('/business')) return '/business/billing';
  return isCoach && inShellPath(pathname) ? '/coach/billing' : null;
};

const Row = ({ label, children, hint }) => (
  <div className="flex flex-col gap-1 border-b border-bm-mutedbg py-3 last:border-b-0 sm:flex-row sm:items-center sm:gap-4">
    <span className="shrink-0 bm-label-l text-bm-muted sm:w-52">{label}</span>
    <span className="flex min-w-0 flex-1 items-center gap-2 bm-body-m text-bm-ink">{children}</span>
    {hint}
  </div>
);

const ProfileField = ({ label, value, editMode, onChange, type = 'text', options, placeholder }) => (
  <Row label={label}>
    {editMode ? (
      type === 'select' ? (
        <Select value={value || ''} onChange={(e) => onChange(e.target.value)} className="max-w-[360px]">
          <option value="">Select {label.toLowerCase()}</option>
          {(options || []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
      ) : (
        <Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || `Enter ${label.toLowerCase()}`} className="max-w-[360px]" />
      )
    ) : value ? (
      value
    ) : (
      <span className="text-bm-tertiary">Not set</span>
    )}
  </Row>
);

// `editingEmail` flips the page into admin mode: an admin / intern editing a
// coach they picked from User Management. It loads that coach into local
// state (so the logged-in admin's own redux user isn't clobbered) and saves
// against the coach's email. With no prop it stays the coach-self flow.
const CoachProfile = ({ editingEmail }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const adminMode = !!editingEmail;
  const loggedInUser = useSelector((state) => state.user.user || {});
  // Reps (Rep Mode) share this page but have no team context: they get
  // their Rep ID and agency instead of the coach team cards.
  const isRep = (loggedInUser.permissions || []).includes('IS_REP') && !(loggedInUser.permissions || []).includes('IS_COACH');
  const inCoachShell = location.pathname.startsWith('/coach');
  const inBrandShell = location.pathname.startsWith('/brand');
  const inShell = inShellPath(location.pathname);
  const codes = useSelector((state) => state.codes?.allCodes || []);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [adminCoach, setAdminCoach] = useState(null);

  // The coach whose profile this page shows: the picked coach in admin mode,
  // else the logged-in user.
  const user = useMemo(() => (adminMode ? adminCoach || {} : loggedInUser), [adminMode, adminCoach, loggedInUser]);

  const getState = () => ({ auth: { token: localStorage.getItem('token') } });

  const loadAdminCoach = async () => {
    try {
      const data = await secureFetch({
        method: 'GET',
        url: `${BASE_URL}/api/users/users/by-email/?email=${encodeURIComponent(editingEmail)}`,
        getState,
      });
      setAdminCoach(data || {});
    } catch (err) {
      toast.error('Could not load coach profile.');
    }
  };

  useEffect(() => {
    dispatch(fetchAllCodes());
  }, [dispatch]);

  useEffect(() => {
    if (adminMode) loadAdminCoach();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminMode, editingEmail]);

  useEffect(() => {
    if (user.email) {
      setForm({
        name: user.name || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        currentTeam: user.options?.currentTeam || '',
      });
    }
  }, [user]);

  const teamOptions = useMemo(
    () =>
      codes
        .filter((c) => c.type === 'team')
        .map((c) => ({ value: c.value, label: c.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [codes],
  );

  const getTeamName = (value) => {
    const team = teamOptions.find((t) => t.value === parseInt(value));
    return team?.label || '';
  };

  const cancelEdit = () => {
    setEditMode(false);
    setForm({ name: user.name, firstName: user.firstName, lastName: user.lastName, currentTeam: user.options?.currentTeam });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await secureFetch({
        method: 'PUT',
        url: `${BASE_URL}/api/users/users/profile/update/`,
        body: {
          email: user.email,
          name: form.name,
          firstName: form.firstName,
          lastName: form.lastName,
          options: {
            currentTeam: form.currentTeam ? parseInt(form.currentTeam) : undefined,
          },
        },
        getState,
      });
      toast.success('Profile updated');
      setEditMode(false);
      // Refresh: in admin mode reload the picked coach locally; otherwise
      // refresh the logged-in user's own redux record.
      if (adminMode) loadAdminCoach();
      else dispatch(fetchUserByEmail(user.email));
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const cv = user.coachVerification || {};
  const cascade = cv.cascade || {};
  const isVerified = cv.status === 'verified';
  const teamRows = [
    ['Sport', [cascade.sport, cascade.gender].filter(Boolean).join(' · ')],
    ['Tier', cascade.tier],
    ['Region', cascade.region],
    ['Governing body / League', cascade.body],
    ['Sub-division', cascade.subDivision],
    ['Class', cascade.classDivision],
    ['Conference', cascade.conference],
    ['League', cascade.leagueDisplayName || cv.league],
  ].filter(([, v]) => v);
  const extras = cv.additionalTeams || [];
  const roleLabel = (user.permissions || []).map((p) => p.replace(/^IS_/, '').replace(/_/g, ' ').toLowerCase()).map((s) => s.charAt(0).toUpperCase() + s.slice(1)).join(', ');
  const subscriptionStatus = user.subscription?.status || 'Free';
  const subscriptionPlan = user.subscription?.plan || 'No active plan';

  // The team and verification cards are for coaches (the admin editor only
  // ever opens a coach). Reps and the other roles have no team context.
  const showCoachCards = adminMode || (user.permissions || []).includes('IS_COACH');
  const billingTo = adminMode ? null : billingPathIn(location.pathname, loggedInUser.permissions || []);

  const eyebrow = adminMode ? 'Admin' : isRep ? 'Rep Mode' : inCoachShell ? 'Coach Mode' : inBrandShell ? 'Brand Mode' : 'Account';
  const title = adminMode ? 'Coach profile' : showCoachCards ? 'Profile & verification' : 'Profile';
  const subtitle = adminMode
    ? `Editing ${user.name || user.email || 'this coach'}'s account details and team settings.`
    : isRep
      ? 'Your account details, Rep ID and agency.'
      : showCoachCards
        ? 'Your account details, verification status and team settings.'
        : 'Your account details and subscription.';

  const content = (
    <div>
      <PageHeader
        eyebrow={eyebrow}
        title={title}
        subtitle={subtitle}
        actions={
          !editMode ? (
            <Btn variant="secondary" onClick={() => setEditMode(true)}>
              Edit profile
            </Btn>
          ) : (
            <span className="flex items-center gap-2">
              <Btn variant="secondary" onClick={cancelEdit}>
                Cancel
              </Btn>
              <Btn onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save changes'}
              </Btn>
            </span>
          )
        }
      />

      <div className="flex flex-col gap-4 pt-2">
        {/* Verification status: pending banner / verified code panel /
            rejected notice. Hidden when the coach hasn't run onboarding. */}
        {showCoachCards && <CoachVerificationPanel verification={user.coachVerification} />}

        <Card padding="px-6 py-5" className="flex flex-col gap-2">
          <Overline>Account information</Overline>
          <div>
            <ProfileField label="Email" value={user.email} editMode={false} />
            <ProfileField label="Full name" value={form.name} editMode={editMode} onChange={(v) => setForm({ ...form, name: v })} />
            <ProfileField label="First name" value={form.firstName} editMode={editMode} onChange={(v) => setForm({ ...form, firstName: v })} />
            <ProfileField label="Last name" value={form.lastName} editMode={editMode} onChange={(v) => setForm({ ...form, lastName: v })} />
            <ProfileField label="Role" value={roleLabel} editMode={false} />
            {isRep && (
              <Row label="Rep ID">
                {user.repCode ? <span className="font-archivo font-bold tracking-[0.08em]">{user.repCode}</span> : <span className="text-bm-tertiary">Not assigned yet</span>}
              </Row>
            )}
            {isRep && <ProfileField label="Agency" value={user.repProfile?.agency || ''} editMode={false} />}
          </div>
        </Card>

        {showCoachCards && (
          <>
            {/* Team settings: selection context from the onboarding cascade. */}
            <Card padding="px-6 py-5" className="flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <Overline>Team settings</Overline>
                <p className="bm-body-s text-bm-muted">Selected during onboarding. Contact support to change.</p>
              </div>
              <div>
                {teamRows.map(([label, value]) => (
                  <Row key={label} label={label}>
                    {value}
                  </Row>
                ))}
                {/* Current team: locked once an admin has verified the coach.
                    Before verification the coach can still swap it; after,
                    the team is the contract their code was issued against,
                    so changes go through support. */}
                {isVerified ? (
                  <Row label="Current team">
                    {cv.currentTeam || getTeamName(form.currentTeam) || <span className="text-bm-tertiary">Not set</span>}
                    <Badge tone="passive" className="ml-1">
                      Locked
                    </Badge>
                  </Row>
                ) : (
                  <ProfileField
                    label="Current team"
                    value={editMode ? form.currentTeam : getTeamName(form.currentTeam)}
                    editMode={editMode}
                    type="select"
                    options={teamOptions}
                    onChange={(v) => setForm({ ...form, currentTeam: v })}
                  />
                )}
                {cv.teamId && (
                  // One "Team ID" label for every source: a coach (also an
                  // AD, a brand or a rep) never reads the data provider's
                  // name (10/19 notes, D16).
                  <Row label="Team ID">
                    <span className="font-mono bm-body-s text-bm-muted">{cv.teamId}</span>
                  </Row>
                )}
              </div>
            </Card>

            {/* Additional teams: always rendered so coaches know the
                program-director feature exists. */}
            <Card padding="px-6 py-5" className="flex flex-col gap-4">
              <div className="flex flex-col gap-1">
                <Overline>Additional teams{extras.length > 0 ? ` · ${extras.length}` : ''}</Overline>
                <p className="bm-body-s text-bm-muted">Program-director access assigned by an admin. Contact support if you should be covering additional teams.</p>
              </div>
              {extras.length === 0 ? (
                <EmptyState>No additional teams assigned yet.</EmptyState>
              ) : (
                <div className="grid gap-3 md:grid-cols-2">
                  {extras.map((t) => (
                    <div key={t.teamId} className="flex flex-col gap-1 rounded-[8px] border border-bm-border bg-bm-subtle px-4 py-[14px]">
                      <span className="bm-h-xs text-bm-ink">{t.currentTeam || t.teamId}</span>
                      {(t.cascade?.leagueDisplayName || t.league) && <span className="bm-body-s text-bm-muted">{t.cascade?.leagueDisplayName || t.league}</span>}
                      <span className="bm-body-s text-bm-tertiary">{[t.cascade?.tier, t.cascade?.body, t.cascade?.subDivision, t.cascade?.conference].filter(Boolean).join(' · ')}</span>
                      {t.teamId && <span className="font-mono bm-body-s text-bm-tertiary">{t.teamId}</span>}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </>
        )}

        <Card padding="px-6 py-5" className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-3">
            <Overline>Subscription</Overline>
            {billingTo && (
              <Btn variant="secondary" size="sm" to={billingTo}>
                Manage billing
              </Btn>
            )}
          </div>
          <div>
            <Row label="Status">
              <span className="capitalize">{subscriptionStatus}</span>
            </Row>
            <Row label="Plan">{subscriptionPlan}</Row>
          </div>
        </Card>
      </div>
    </div>
  );

  if (inShell) return content;
  return <div className="bm-root min-h-screen bg-white px-5 pb-[70px] sm:px-8 lg:px-12 min-[1440px]:px-[122px]">{content}</div>;
};

export default CoachProfile;
